import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { adminApi, AdminHeader } from "@/lib/adminApi";

const inputCls = "px-3 py-2 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-sm text-white placeholder:text-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-violet)]";

const AdminSources = () => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", feed_url: "", category: "", priority: "medium" });

  const load = () => adminApi("/admin/sources").then(setSources).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await adminApi("/admin/sources", { method: "POST", body: form });
      toast.success("Source added");
      setForm({ name: "", url: "", feed_url: "", category: "", priority: "medium" });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    await adminApi(`/admin/sources/${id}`, { method: "DELETE" }).catch((e) => toast.error(e.message));
    toast.success("Source removed");
    load();
  };

  return (
    <div>
      <AdminHeader title="Trusted Sources" subtitle={`${sources.length} sources the AI Curator scans twice a day. Leave the feed blank to auto-track the site via Google News.`} testId="sources-heading" />
      <form onSubmit={add} className="bg-[var(--bg-card)] border-glow rounded-xl p-5 mb-6 grid md:grid-cols-[1fr_1.2fr_1.2fr_1fr_auto_auto] gap-3" data-testid="source-form">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} data-testid="source-name-input" />
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Website https://" className={inputCls} data-testid="source-url-input" />
        <input value={form.feed_url} onChange={(e) => setForm({ ...form, feed_url: e.target.value })} placeholder="RSS feed (optional)" className={inputCls} data-testid="source-feed-input" />
        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className={inputCls} data-testid="source-category-input" />
        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls} data-testid="source-priority-select">
          <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <button type="submit" className="flex items-center justify-center gap-1.5 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors" data-testid="source-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>
      <div className="bg-[var(--bg-card)] border-glow rounded-xl divide-y divide-[rgba(124,58,237,0.08)]">
        {sources.map((s) => (
          <div key={s._id} className="flex items-center justify-between px-5 py-3.5" data-testid={`source-row-${s._id}`}>
            <div className="min-w-0">
              <p className="text-sm text-white font-medium">{s.name}</p>
              {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--accent-violet-light)] inline-flex items-center gap-1 truncate"><ExternalLink className="w-3 h-3" />{s.url}</a>}
              <p className="text-[10px] text-[var(--text-secondary)]/70 mt-0.5">{s.feed_url ? "RSS feed" : "Auto-tracked via Google News"}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[var(--text-secondary)]">{s.category}</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">{s.priority}</span>
              <button onClick={() => remove(s._id)} className="text-[var(--text-secondary)] hover:text-red-400 transition-colors" data-testid={`source-delete-${s._id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSources;
